/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/notification';
import uploadFileIcon from '../../../assets/images/upload-file.svg';
import attachFileIcon from '../../../assets/images/attach-file.svg';
import Loader from '../../../assets/images/loader.svg';
import Select from '../../form/Select';
import BuildGenericUploadForm from './BuildGenericUploadForm';

import './UploadCredentialModal.scss';

const UploadGenericCredentialModal = ({
	caregiverId,
	managementCompanyId,
	onClose,
	onUpload,
	credentialType,
	enabledCredentials,
	uploadCredentialMutation,
	credentialByTypeQuery,
	showPLInfo,
	allowedCredentials,
	isAttachmentRequired = true,
	credentialData,
	DownloadButton
}) => {
	const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
	const dispatch = useDispatch();

	const [formFields, setFormFields] = useState([]);
	const [data, setData] = useState({});
	const [credType, setCredType] = useState('');
	const [validationErrors, setValidationErrors] = useState([]);
	const [fileError, setFileError] = useState('');
	const [showBGCheckInfo, setShowBGCheckInfo] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);

	const formFieldKeys = formFields?.map((field) => field.name) ?? [];
	const isEdit = !!credentialData;

	const updateData = (name, value) => {
		setData({ ...data, [name]: value });
		setHasChanges(true);
	};

	const { data: credentialByType, error: credentialByTypeError, refetch } = credentialByTypeQuery(
		{ caregiverId, managementCompanyId, credType: data.credential || credentialType, source: 'modal' },
		{
			refetchOnMountOrArgChange: true
		}
	);

	const [uploadCredential, { isLoading }] = uploadCredentialMutation();

	useEffect(() => {
		setValidationErrors([]);
		setFormFields([]);
		if (isEdit) {
			setData({ ...credentialData, status: credentialData.status_code, credential: credentialType });
		}
	}, []);

	useEffect(() => {
		if (credentialByTypeError) {
			setFormFields([]);
			if (Array.isArray(credentialByTypeError?.data?.errors) && credentialByTypeError.data.errors[0]?.message) {
				dispatch(showNotification('error', credentialByTypeError.data.errors[0].message));
			}
		}
	}, [credentialByTypeError, dispatch]);

	useEffect(() => {
		setFormFields(credentialByType?.editUIConfig);
		setCredType(data.credential);
	}, [credentialByType, data.credential]);

	const getValidationErrorMessage = useCallback(
		(fieldName) => {
			const error = validationErrors.find((item) => item.field === fieldName);
			return error && error.message;
		},
		[validationErrors]
	);

	useEffect(() => {
		setFileError(getValidationErrorMessage('attachment_id'));
	}, [validationErrors, getValidationErrorMessage]);

	const handleUploadFile = async (event) => {
		event.stopPropagation();
		event.preventDefault();
		const file = event.target.files[0];
		const fileSizeInMb = Math.round(file.size / (1024 * 1024));

		if (file && fileSizeInMb > 50) {
			event.target.value = null;
			dispatch(showNotification('error', 'File must not exceed 50MB'));
			return;
		}
		if (file && ALLOWED_MIME_TYPES.indexOf(file.type.toLowerCase()) === -1) {
			event.target.value = null;
			dispatch(showNotification('error', 'Error uploading files. Only PDF, JPG and PNG files are allowed'));
			return;
		}
		if (!file) {
			event.target.value = null;
			return;
		}
		updateData('attachment', file);
		setFileError('');
	};

	const handleSubmitForm = async () => {
		const allErrors = [];
		if (isLoading) {
			return;
		}
		try {
			if (!credType) {
				allErrors.push({
					field: 'credential',
					message: '"Credential" is required.'
				});
			}
			if (!data.attachment && isAttachmentRequired) {
				allErrors.push({
					field: 'attachment_id',
					message: 'Please select an attachment'
				});
			}
			if (allErrors.length > 0) {
				setValidationErrors(allErrors);
				return;
			}

			const formData = new FormData();

			formFieldKeys.forEach((key) => {
				const value = data[key];
				if (key === 'attachment') {
					if (!value && !isAttachmentRequired) {
						return;
					}

					formData.append(key, value);
					return;
				}

				formData.append(key, value?.toString()?.trim() ?? '');
			});

			await uploadCredential({
				caregiverId,
				managementCompanyId,
				credType,
				formData,
				credentialId: credentialData ? credentialData.id : undefined
			}).unwrap();
			if (onUpload) {
				onUpload(credType);
			}
			onClose();
		} catch (error) {
			const defaultError = 'There was a problem uploading your credential.';
			const { errors, validationError } = error?.data || {};
			if (validationError && errors.length) {
				allErrors.push(...errors);
				setValidationErrors(allErrors);
				if (errors.length === 1 && !errors[0].field && errors[0].message) {
					// credential not supported
					dispatch(showNotification('error', errors[0].message));
				}
			}
			if (!validationError) {
				dispatch(showNotification('error', defaultError));
				onClose();
			}
		}
	};

	const getCredentialDropdownOptions = () => {
		return enabledCredentials
			?.filter((cred) => {
				if (['PROFESSIONAL_LICENSE', 'FACILITY_APPROVED_WORKER', 'PROVISIONED_WORKER'].includes(cred.credential_type)) {
					return false;
				}

				// when page is filtered we have allowedCredentials
				if (allowedCredentials && !allowedCredentials.some((elem) => elem.code === cred.credential_type)) {
					return false;
				}
				return true;
			})
			?.map((option) => ({ label: option.name, value: option.credential_type }));
	};

	const handleCredentialChange = (credentialCode) => {
		const newValue = { credential: credentialCode, attachment: data.attachment };

		// default to selecting Care related employer for new references
		if (!newValue.relationship && newValue.credential === 'REFERENCES') {
			newValue.relationship = 'CARE_RELATED_EMPLOYER';
		}

		// remove other data than the credential type, attachment and the default values
		setData(newValue);
		setValidationErrors([]);
	};

	const buildAllowedSubtypes = (credentials) => {
		const targetCred = credentialType || data.credential;
		const selectedCred = credentials?.find((cred) => cred.code === targetCred);
		if (targetCred && selectedCred.subtypes.length > 0) {
			return selectedCred.subtypes.reduce((acc, subtype) => {
				if (subtype.sub_type && !subtype.exceptions_ok) {
					acc.push({
						code: subtype.sub_type,
						name: subtype.sub_name
					});
				}
				return acc;
			}, []);
		}
		return [];
	};

	useEffect(() => {
		if (credentialType) {
			if (!isEdit) {
				handleCredentialChange(credentialType);
			}
			refetch();
		}
	}, [credentialType, isEdit]);

	useEffect(() => {
		if (data.type === 'CHECKR_INKY_STATEWIDE_BACKGROUND_CHECK') {
			setShowBGCheckInfo(true);
			return;
		}
		setShowBGCheckInfo(false);
	}, [data.credential, data.type]);

	return (
		<Modal
			show
			centered
			className={classNames('upload-credential-modal', { 'is-loading': isLoading })}
			backdrop="static"
			onHide={onClose}
			keyboard={false}
		>
			<Modal.Header closeButton>
				<Modal.Title>{isEdit ? 'Edit credential' : 'Select and upload credential'}</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<div className="upper-container">
					{isLoading && <div className="gray-overlay" />}
					<div className="left-side">
						<div className="first-row">
							<label className="select-file-btn btn-light-purple" htmlFor="uploadFile">
								<img src={uploadFileIcon} alt="" />
								<span>Select file from computer</span>
								<input type="file" id="uploadFile" style={{ display: 'none' }} onChange={handleUploadFile} />
							</label>

							<span className="upload-info">
								File must not exceed 50 MB. To replace the currently selected file, select a new file to upload.
							</span>
						</div>

						{fileError && <div className="file-error">{fileError}</div>}

						{showPLInfo && (
							<div className="info-container">
								<span className="info-icon" />
								<span>NOTE: Professional Licenses cannot be uploaded here.</span>
							</div>
						)}

						{data.attachment && (
							<div className="file-attachment">
								<img src={attachFileIcon} alt="" />
								<div className="attachment-name">
									{isEdit ? data.attachment?.name || 'File attached' : data.attachment?.name}
								</div>
								{!data.attachment?.name && DownloadButton}
							</div>
						)}
					</div>
					<div className="right-side">
						<div className="upload-form">
							<div
								className={classNames(`form-field credential-field`, {
									'validation-error': !!getValidationErrorMessage('credential')
								})}
							>
								<Select
									name="credential"
									label="Credential"
									placeholder="Select..."
									handleChange={(value) => handleCredentialChange(value.credential)}
									options={getCredentialDropdownOptions()}
									isDisabled={credentialType && data.credential}
									value={data.credential}
									className={`form-select ${credentialType && data.credential ? 'input-disabled' : ''}`}
									isSearchable
								/>
								<div className="error-message">{getValidationErrorMessage('credential')}</div>
							</div>

							<BuildGenericUploadForm
								formFields={formFields || []}
								data={data}
								updateData={updateData}
								getValidationErrorMessage={getValidationErrorMessage}
								allowedSubtypes={
									allowedCredentials && Object.keys(allowedCredentials).length > 0
										? buildAllowedSubtypes(allowedCredentials)
										: []
								}
							/>
						</div>
						{showBGCheckInfo && (
							<div className="bg-info-container">
								<span className="info-icon" />
								<span>
									Please include results from Checkr, IN and KY statewide searches, FACIS III, and Education
									Verification in your upload.
								</span>
							</div>
						)}
					</div>
				</div>
			</Modal.Body>

			<Modal.Footer>
				<div className="buttons">
					<button type="button" className="btn-purple__text" onClick={onClose} disabled={isLoading}>
						Cancel
					</button>
					<button
						type="button"
						className="btn-purple"
						onClick={handleSubmitForm}
						disabled={isLoading || (!hasChanges && isEdit)}
					>
						{isLoading ? (
							<>
								<img aria-hidden src={Loader} alt="loader" className="button-loading-spinner" /> Uploading...
							</>
						) : (
							<>
								{isEdit && <>Save changes</>}
								{!isEdit && <>Upload</>}
							</>
						)}
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default UploadGenericCredentialModal;
